<script setup>
import moment from 'moment'
import Button from 'primevue/button'
import Popover from 'primevue/popover'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { trans } from 'laravel-vue-i18n'
import { useReservationsTopbarStore } from '../../../../../state/reservationsTopbar.js'
import { useConfirm } from 'primevue/useconfirm'
import { router, usePage } from '@inertiajs/vue3'
import CalendarGrid from './CalendarGrid.vue'
import CalendarDayView from './CalendarDayView.vue'
import CalendarWeekView from './CalendarWeekView.vue'
import CalendarAgendaView from './CalendarAgendaView.vue'
import CalendarYearView from './CalendarYearView.vue'
import MobileCalendarView from './MobileCalendarView.vue'
import Dialog from 'primevue/dialog'
import ClientMessageDialog from './ClientMessageDialog.vue'
import ReservationDetail from '../dashboard/ReservationDetail.vue'
import ReservationHoverPreview from './ReservationHoverPreview.vue'
import ReservationEventDrawer from './ReservationEventDrawer.vue'
import ReservationsTodayStrip from './ReservationsTodayStrip.vue'
import NewReservationPopover from './NewReservationPopover.vue'

const props = defineProps({
    reservations:        { type: Array,   default: () => [] },
    reservationForms:    { type: Array,   default: () => [] },
    allEmployees:        { type: Array,   default: () => [] },
    pendingChangeRequest:{ type: Array,   default: () => [] },
    mobileOnly:          { type: Boolean, default: false },
    visibleFormIds:      { type: Set,     default: () => new Set() },
    visibleColleagueIds: { type: Set,     default: () => new Set() },
    selectedDate:        Object,  // moment, řízeno rodicem
    currentView:         { type: String, default: 'mesic' },
})

const emit = defineEmits(['prev', 'next', 'today', 'update:currentView', 'select-month', 'toggle-form', 'toggle-colleague'])

// ─── Segment views ────────────────────────────────────────────────

const VIEWS = [
    { key: 'den',    label: trans('reservations.cal_day') },
    { key: 'tyden',  label: trans('reservations.cal_week') },
    { key: 'mesic',  label: trans('reservations.cal_month') },
    { key: 'agenda', label: trans('reservations.cal_agenda') },
    { key: 'rok',    label: trans('reservations.cal_year') },
]

// ─── Toolbar title ────────────────────────────────────────────────

const MK = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec']
const MONTHS_CZ  = MK.map(k => trans(`common.months.${k}`))
const MONTHS_GEN = MK.map(k => trans(`common.months_gen.${k}`))
const DOW_LONG   = ['sun','mon','tue','wed','thu','fri','sat'].map(k => trans(`common.dow_long.${k}`))

const headerTitle = computed(() => {
    const d = props.selectedDate
    if (!d) return ''
    if (props.currentView === 'den') {
        const dow   = DOW_LONG[d.day()]
        const day   = d.date()
        const month = MONTHS_GEN[d.month()]
        return `${dow} ${day}. ${month} ${d.year()}`
    }
    if (props.currentView === 'tyden') {
        const start = d.clone().startOf('isoWeek')
        const end   = d.clone().endOf('isoWeek')
        if (start.month() === end.month()) {
            return `${start.date()}–${end.date()}. ${MONTHS_GEN[start.month()]} ${start.year()}`
        }
        return `${start.date()}. ${MONTHS_GEN[start.month()]} – ${end.date()}. ${MONTHS_GEN[end.month()]} ${end.year()}`
    }
    if (props.currentView === 'mesic') {
        return `${MONTHS_CZ[d.month()]} ${d.year()}`
    }
    if (props.currentView === 'agenda') {
        return `${trans('reservations.cal_agenda')} · ${MONTHS_CZ[d.month()]} ${d.year()}`
    }
    if (props.currentView === 'rok') {
        return String(d.year())
    }
    return ''
})

// ─── Month picker (mobilní topbar) ───────────────────────────────
const monthPickerOpen = ref(false)
const pickerYear      = ref(moment().year())

function openMonthPicker() {
    pickerYear.value = props.selectedDate?.year() ?? moment().year()
    monthPickerOpen.value = true
}

function selectPickerMonth(monthIndex) {
    const date = moment().year(pickerYear.value).month(monthIndex).date(1)
    emit('select-month', date)
    monthPickerOpen.value = false
}

// ─── Topbar store registrace ──────────────────────────────────────
const topbarStore = useReservationsTopbarStore()
watch(headerTitle, title => { topbarStore.headerTitle = title }, { immediate: true })
onMounted(() => topbarStore.register({
    prev:       () => emit('prev'),
    next:       () => emit('next'),
    openPicker: openMonthPicker,
}))
onUnmounted(() => topbarStore.unregister())

// ─── Měsíční grid ─────────────────────────────────────────────────

const selectedStatus = ref('all')
const statusOptions = [
    { label: trans('reservations.filter_all'),      value: 'all',      icon: 'pi pi-list',         severity: 'contrast' },
    { label: trans('reservations.filter_accepted'), value: 'accepted', icon: 'pi pi-check-circle', severity: 'success' },
    { label: trans('reservations.filter_pending'),  value: 'pending',  icon: 'pi pi-clock',        severity: 'info' },
    { label: trans('reservations.filter_rejected'), value: 'rejected', icon: 'pi pi-times-circle', severity: 'danger' },
]
const filtersPopover = ref()
const reactivityKey  = ref(0)
const days           = ref([])
const daysShadow     = ref([])

const generateMonthGrid = () => {
    const m = props.selectedDate ?? moment()
    const startOfMonth  = m.clone().startOf('month')
    const startDayIndex = startOfMonth.isoWeekday()
    const daysInMonth   = m.daysInMonth()
    const grid = Array.from({ length: startDayIndex - 1 }, () => null)

    for (let i = 1; i <= daysInMonth; i++) {
        const date = m.clone().date(i).format('YYYY-MM-DD')
        grid.push({
            day: i,
            date,
            reservations: props.reservations
                .filter(r => selectedStatus.value === 'all' || r.state === selectedStatus.value)
                .filter(r => props.visibleFormIds.size === 0 || props.visibleFormIds.has(r.id_reservation_forms))
                .filter(r => {
                    if (r.date) return r.date === date
                    if (r.date_start && r.date_end) return moment(date).isBetween(r.date_start, r.date_end, 'day', '[]')
                    return false
                }),
        })
    }

    while (grid.length % 7 !== 0) grid.push(null)
    days.value = grid
    daysShadow.value = JSON.parse(JSON.stringify(grid))
}

onMounted(() => generateMonthGrid())

watch(() => props.reservations,    () => { generateMonthGrid(); reactivityKey.value++ }, { deep: true })
watch(() => props.selectedDate,    () => { generateMonthGrid(); reactivityKey.value++ })
watch(() => props.visibleFormIds,  () => { generateMonthGrid(); reactivityKey.value++ })
watch(selectedStatus,              () => { generateMonthGrid(); reactivityKey.value++ })

// ─── New reservation popover ──────────────────────────────────────

const newResOpen   = ref(false)
const newResAnchor = ref(null)
const newResBtnRef = ref(null)

function openNewRes() {
    newResAnchor.value = newResBtnRef.value?.getBoundingClientRect() ?? null
    newResOpen.value = true
}
defineExpose({ openNewRes })

// ─── Hover preview ────────────────────────────────────────────────

const hoveredReservation = ref(null)
function onHoverReservation(data) { hoveredReservation.value = data }

// ─── Event drawer ─────────────────────────────────────────────────

const drawerReservation = ref(null)

function openDrawer(reservation) {
    hoveredReservation.value = null
    drawerReservation.value = reservation
}
function closeDrawer() { drawerReservation.value = null }

const page = usePage()
watch(() => page.url, () => { closeDrawer() })

// Detail rezervace se otevírá jako aside (viz AsideComponent) nad stejnou ReservationsView
// stránkou — přepínač pohledů a tlačítko "Nová" proto musí zmizet i bez opuštění komponenty.
const isAsideOpen = computed(() => !!page.props.__neo_aside)

const currentEvent = ref(null)
const mobileEvent  = ref(false)

function toggleEvent($event, reservation) {
    if (window.innerWidth >= 1024) {
        openDrawer(reservation)
    } else {
        currentEvent.value = reservation
        mobileEvent.value  = true
    }
}

// ─── Drag & drop change ───────────────────────────────────────────

const confirm = useConfirm()
const notificationMessage = ref('')
const doSend = ref(false)

const captureReservationChange = ({ reservation, newDate, newDateStart, newDateEnd }) => {
    if (newDateStart) {
        doMultidayReservationChange(reservation, newDateStart, newDateEnd)
    } else if (newDate) {
        doReservationChange(reservation, newDate)
    }
}

function onReservationChanged({ reservation, newDate, newTimeStart, newTimeEnd }) {
    doReservationChange(reservation, newDate, newTimeStart, newTimeEnd)
}

const doMultidayReservationChange = (reservation, newDateStart, newDateEnd) => {
    reservation.date_start = newDateStart
    reservation.date_end = newDateEnd
    router.post(`/app/forms/event/${reservation.id}/change-range`, {
        dateStart: newDateStart, dateEnd: newDateEnd,
    }, { onSuccess: () => { generateMonthGrid(); reactivityKey.value++ } })
}

const doReservationChange = (reservation, newDate, newTimeStart = null, newTimeEnd = null) => {
    if (reservation.reservation_forms?.has_changed_email_template) {
        confirm.require({
            group: 'confirmation-dialog',
            accept: () => {
                reservation.date = newDate
                reservation.formatted_date = moment(reservation.date).format('DD.MM.YYYY')
                daysShadow.value = JSON.parse(JSON.stringify(days.value))
                router.post(`/app/forms/event/${reservation.id}/change`, {
                    date: newDate, timeStart: newTimeStart, timeEnd: newTimeEnd,
                    message: doSend.value ? notificationMessage.value : null,
                }, { onSuccess: () => { generateMonthGrid(); reactivityKey.value++ } })
                doSend.value = false; notificationMessage.value = ''
            },
            reject: () => {
                days.value = JSON.parse(JSON.stringify(daysShadow.value))
                notificationMessage.value = ''
            },
        })
    } else {
        reservation.date = newDate
        reservation.formatted_date = moment(reservation.date).format('DD.MM.YYYY')
        router.post(`/app/forms/event/${reservation.id}/change`, {
            date: newDate, timeStart: newTimeStart, timeEnd: newTimeEnd, message: null,
        }, { onSuccess: () => { generateMonthGrid(); reactivityKey.value++ } })
        doSend.value = false; notificationMessage.value = ''
    }
}
</script>

<template>
    <ClientMessageDialog v-model="notificationMessage" v-model:do-send="doSend"/>

    <!-- Desktop topbar akce -->
    <Teleport to="#topbar-page-actions">
        <div class="flex items-center gap-2">

            <!-- Desktop navigace (>= lg) -->
            <div class="hidden lg:flex items-center gap-2">
                <button
                    class="px-3 py-1.5 rounded-lg text-sm font-medium border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-600 bg-white dark:bg-neutral-800"
                    @click="emit('today')"
                >{{ $t('reservations.cal_today') }}</button>

                <div class="flex gap-0.5">
                    <button
                        class="w-8 h-8 rounded-full flex items-center justify-center text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        @click="emit('prev')"
                    ><i class="pi pi-chevron-left !text-xs"/></button>
                    <button
                        class="w-8 h-8 rounded-full flex items-center justify-center text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        @click="emit('next')"
                    ><i class="pi pi-chevron-right !text-xs"/></button>
                </div>

                <button
                    :key="reactivityKey + currentView"
                    class="flex items-center gap-1 text-sm font-semibold text-neutral-900 dark:text-white tracking-tight ml-1 select-none hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    @click="openMonthPicker"
                >
                    {{ headerTitle }}
                    <i class="pi pi-chevron-down !text-[10px] text-neutral-400 dark:text-neutral-500"/>
                </button>

                <div class="w-px h-5 bg-neutral-200 dark:bg-neutral-700 mx-1"/>

                <!-- Filtry (jen v měsíčním pohledu) -->
                <template v-if="currentView === 'mesic'">
                    <Button
                        icon="pi pi-sliders-h" :label="$t('reservations.filter_btn')" size="small" outlined severity="secondary"
                        @click="filtersPopover.toggle($event)"
                    />
                    <Popover ref="filtersPopover">
                        <div class="flex flex-col gap-3 w-64 p-1">
                            <span class="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider px-1">{{ $t('reservations.filter_status_label') }}</span>
                            <div class="flex flex-wrap gap-1.5">
                                <Button
                                    v-for="s in statusOptions" :key="s.value"
                                    :label="s.label" :icon="s.icon" :severity="s.severity"
                                    :outlined="selectedStatus !== s.value"
                                    size="small" rounded
                                    @click="selectedStatus = s.value"
                                />
                            </div>
                        </div>
                    </Popover>
                </template>

                <!-- Segment control -->
                <div v-if="!isAsideOpen" class="hidden lg:flex bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg p-0.5 gap-px">
                    <button
                        v-for="v in VIEWS" :key="v.key"
                        class="px-3 py-1 rounded-md text-sm font-medium transition-colors"
                        :class="currentView === v.key
                            ? 'bg-white dark:bg-neutral-700 text-neutral-800 dark:text-white shadow-sm'
                            : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'"
                        @click="emit('update:currentView', v.key)"
                    >{{ v.label }}</button>
                </div>
            </div>

            <button
                v-if="!isAsideOpen"
                ref="newResBtnRef"
                class="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
                @click="openNewRes"
            >
                <i class="pi pi-plus !text-xs"/>{{ $t('reservations.new_btn') }}
            </button>
        </div>
    </Teleport>

    <!-- Month picker — mobilní bottom sheet -->
    <Transition name="month-sheet">
        <div v-if="monthPickerOpen" class="fixed inset-0 z-[60] flex flex-col justify-end">
            <div class="absolute inset-0 bg-black/30" @click="monthPickerOpen = false"/>
            <div class="relative bg-white dark:bg-neutral-900 rounded-t-3xl">
                <div class="w-12 h-1 bg-neutral-200 dark:bg-neutral-700 rounded-full mx-auto mt-3"/>

                <div class="flex items-center justify-between px-6 py-4">
                    <button
                        class="w-9 h-9 flex items-center justify-center rounded-full text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        @click="pickerYear--"
                    ><i class="pi pi-chevron-left !text-sm"/></button>
                    <span class="text-[17px] font-semibold text-neutral-900 dark:text-white">{{ pickerYear }}</span>
                    <button
                        class="w-9 h-9 flex items-center justify-center rounded-full text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        @click="pickerYear++"
                    ><i class="pi pi-chevron-right !text-sm"/></button>
                </div>

                <div class="grid grid-cols-3 gap-2 px-4 pb-8">
                    <button
                        v-for="(name, i) in MONTHS_CZ"
                        :key="i"
                        class="py-3 rounded-xl text-[14px] font-medium transition-colors"
                        :class="pickerYear === props.selectedDate?.year() && i === props.selectedDate?.month()
                            ? 'bg-blue-600 text-white'
                            : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'"
                        @click="selectPickerMonth(i)"
                    >{{ name }}</button>
                </div>
            </div>
        </div>
    </Transition>

    <div class="flex-1 h-full flex flex-col min-w-0 min-h-0 overflow-hidden">

        <!-- ── Today strip (jen měsíc) ─────────────────────────── -->
        <ReservationsTodayStrip
            v-if="!mobileOnly && currentView === 'mesic'"
            :reservations="reservations"
            :visible-form-ids="visibleFormIds"
            @open-reservation="openDrawer"
            @new-reservation="openNewRes"
        />

        <!-- ── Views ────────────────────────────────────────────── -->
        <CalendarGrid
            v-if="!mobileOnly && currentView === 'mesic'"
            :selected-month="selectedDate"
            :days="days"
            @capture-reservation-change="captureReservationChange"
            @toggle-event="toggleEvent"
            @hover-reservation="onHoverReservation"
        />

        <CalendarDayView
            v-if="!mobileOnly && currentView === 'den'"
            :reservations="reservations"
            :all-employees="allEmployees"
            :visible-form-ids="visibleFormIds"
            :visible-colleague-ids="visibleColleagueIds"
            :selected-date="selectedDate"
            @open-reservation="openDrawer"
            @hover-reservation="onHoverReservation"
            @reservation-changed="onReservationChanged"
        />

        <CalendarWeekView
            v-if="!mobileOnly && currentView === 'tyden'"
            :reservations="reservations"
            :visible-form-ids="visibleFormIds"
            :selected-date="selectedDate"
            @open-reservation="openDrawer"
            @hover-reservation="onHoverReservation"
            @reservation-changed="onReservationChanged"
        />

        <CalendarAgendaView
            v-if="!mobileOnly && currentView === 'agenda'"
            :reservations="reservations"
            :visible-form-ids="visibleFormIds"
            :selected-date="selectedDate"
            @open-reservation="openDrawer"
        />

        <CalendarYearView
            v-if="!mobileOnly && currentView === 'rok'"
            :reservations="reservations"
            :visible-form-ids="visibleFormIds"
            :selected-date="selectedDate"
            @select-month="emit('select-month', $event)"
        />

        <!-- Hover preview -->
        <ReservationHoverPreview :hovered="hoveredReservation"/>

        <!-- New reservation popover -->
        <NewReservationPopover
            :open="newResOpen"
            :forms="reservationForms"
            :anchor-rect="newResAnchor"
            @close="newResOpen = false"
        />

        <!-- Event drawer -->
        <ReservationEventDrawer
            :reservation="drawerReservation"
            @close="closeDrawer"
            @status-changed="closeDrawer"
        />

        <!-- Mobilní pohled -->
        <MobileCalendarView
            :selected-month="selectedDate"
            :reservations="reservations"
            :reservation-forms="reservationForms"
            :all-employees="allEmployees"
            :visible-form-ids="visibleFormIds"
            :visible-colleague-ids="visibleColleagueIds"
            :days="days"
            :show-always="mobileOnly"
            @prev-month="emit('prev')"
            @next-month="emit('next')"
            @select-month="emit('select-month', $event)"
            @toggle-form="(id) => $emit('toggle-form', id)"
            @toggle-colleague="(id) => $emit('toggle-colleague', id)"
        />

        <!-- Mobile detail dialog -->
        <Dialog v-model:visible="mobileEvent" modal maximizable :show-header="false">
            <template #container>
                <ReservationDetail
                    v-if="currentEvent"
                    :reservation="currentEvent"
                    @change-reservation="(d, ts, te) => doReservationChange(currentEvent, d, ts, te)"
                >
                    <template #buttons>
                        <Button @click="mobileEvent = false" rounded severity="secondary" icon="pi pi-times" size="small"/>
                    </template>
                </ReservationDetail>
            </template>
        </Dialog>
    </div>
</template>

<style scoped>
.month-sheet-enter-active, .month-sheet-leave-active { transition: opacity .25s ease; }
.month-sheet-enter-active .relative, .month-sheet-leave-active .relative { transition: transform .28s cubic-bezier(.25,1,.3,1); }
.month-sheet-enter-from { opacity: 0; }
.month-sheet-leave-to   { opacity: 0; }
.month-sheet-enter-from .relative, .month-sheet-leave-to .relative { transform: translateY(100%); }
</style>
